import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAxiosFetch from "../common/useAxiosFetch";
import LoadPage from "../common/LoadPage";
import moment from "moment";
import { DEFAULT_RANGE_BOUNDS, getScoreBorderClass, getRiskColorVar } from "../common/helpers";

const WarningBadge = ({ warning }) => {
  const colors = {
    high: { bg: '#fff0f0', border: '#ffcdd2', color: 'var(--red-risk-score)', icon: 'icon-Risico' },
    medium: { bg: '#fff8e1', border: '#ffe082', color: '#e65100', icon: 'icon-alert' },
    low: { bg: '#e8f5e9', border: '#c8e6c9', color: '#2e7d32', icon: 'icon-info' }
  };
  const style = colors[warning.level] || colors.low;
  
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '10px 14px', borderRadius: 6,
      backgroundColor: style.bg, border: `1px solid ${style.border}`,
      fontSize: 13
    }}>
      <i className={style.icon} style={{ color: style.color, fontSize: 14 }} />
      <span style={{ color: style.color, fontWeight: 500 }}>{warning.message}</span>
    </div>
  );
};

const KpiCard = ({ icon, label, value, subtitle, color }) => (
  <div style={{
    flex: '1 1 0', minWidth: 0, textAlign: 'center', padding: '12px 6px',
    borderRight: '1px solid #eee'
  }}>
    <i className={icon} style={{ fontSize: 16, opacity: 0.7, color: color || 'inherit' }} />
    <div style={{ fontSize: 10, opacity: 0.7, marginTop: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 'bold', marginTop: 2, color: color || 'inherit' }}>{value}</div>
    {subtitle && <div style={{ fontSize: 10, opacity: 0.7, marginTop: 2 }}>{subtitle}</div>}
  </div>
);

const ProgressBar = ({ value, max, color, label, height = 8 }) => {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div style={{ marginBottom: 8 }}>
      {label && (
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
          <span style={{ fontWeight: 500 }}>{label}</span>
          <span style={{ opacity: 0.7 }}>{value}/{max}</span>
        </div>
      )}
      <div style={{ width: '100%', height, backgroundColor: '#eee', borderRadius: height / 2 }}>
        <div style={{
          width: `${pct}%`, height: '100%', borderRadius: height / 2,
          backgroundColor: color || 'var(--text-color)',
          transition: 'width 0.3s ease'
        }} />
      </div>
    </div>
  );
};

const SeverityDot = ({ count, color, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
    <div style={{
      width: 10, height: 10, borderRadius: '50%', backgroundColor: color, flexShrink: 0
    }} />
    <span style={{ fontWeight: 600 }}>{count}</span>
    <span style={{ opacity: 0.7 }}>{label}</span>
  </div>
);

const SectionTitle = ({ icon, title, count }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
    <i className={icon} style={{ fontSize: 16, opacity: 0.6 }} />
    <span style={{ fontSize: 16, fontWeight: 700 }}>{title}</span>
    {count !== undefined && <span style={{ fontSize: 12, opacity: 0.5, fontWeight: 400 }}>({count})</span>}
  </div>
);

// Shared styles to fix content-bubble (float:left + width:100%) inside CSS grid
const gridStyle = (cols = '1fr 1fr') => ({
  display: 'grid', gridTemplateColumns: cols, gap: 16, clear: 'both'
});
const bubbleInGrid = { float: 'none', width: 'auto', minWidth: 0 };

const ViewEnvironmentPage = () => {
  const [searchParams] = useSearchParams();
  const envId = searchParams.get("envId");
  const { data, loading } = useAxiosFetch("/api/environments/" + envId + "?details=true");
  const [activeTab, setActiveTab] = useState('overview');

  if (loading || !data) return <LoadPage />;

  const environmentName = data.name === "www" ? "main" : data.name;
  const insights = data.insights || {};
  const warnings = insights.warnings || [];
  const rangeBounds = insights.rangeBounds || DEFAULT_RANGE_BOUNDS;
  const users = data.users || [];
  const orgs = data.organizations || [];
  const projects = data.projects || [];
  const risks = data.risks || [];

  // Subscription status
  const now = new Date().getTime();
  const plan = data.subscriptionPlan || {};
  const hasActiveSubscription = data.subscriptionExpires && data.subscriptionExpires > now;
  const hasActiveTrial = data.trialExpires && data.trialExpires > now;
  const planId = plan.planId || 'none';
  const isSuperEnvironment = data.name === 'www' || data.name === 'main' || data.name === 'knmt';

  let statusText, statusColor;
  if (isSuperEnvironment) { statusText = 'Super'; statusColor = '#9C27B0'; }
  else if (planId === 'free') { statusText = 'Free'; statusColor = '#2196F3'; }
  else if (hasActiveSubscription) { statusText = 'Active'; statusColor = '#4CAF50'; }
  else if (hasActiveTrial) { statusText = 'Trial'; statusColor = '#FF9800'; }
  else if (planId === 'trial') { statusText = 'Expired'; statusColor = '#f44336'; }
  else { statusText = 'Inactive'; statusColor = '#9e9e9e'; }

  const tabs = [
    { key: 'overview', label: 'Overview', icon: 'icon-overview' },
    { key: 'risks', label: 'Risks', icon: 'icon-Risico' },
    { key: 'domains', label: 'Domains', icon: 'icon-Projecten' },
    { key: 'users', label: 'Users', icon: 'icon-users' }
  ];

  return (
    <div id="content">
      {/* Header */}
      <div className="content-bubble no-float" style={{ marginTop: 0, padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 20, fontWeight: 700 }}>
              <a href={"https://" + environmentName + ".risqui.nl"} target="_blank" rel="noreferrer" style={{ color: 'var(--text-color)', textDecoration: 'none' }}>
                {environmentName}
              </a>
            </span>
            <span style={{
              display: 'inline-block', padding: '2px 10px', borderRadius: 12,
              fontSize: 11, fontWeight: 700, color: '#fff',
              backgroundColor: statusColor
            }}>
              {statusText}
            </span>
            <span style={{
              display: 'inline-block', padding: '2px 10px', borderRadius: 12,
              fontSize: 11, fontWeight: 600, color: 'var(--text-color)',
              backgroundColor: 'var(--light-blue-bg-color)', textTransform: 'capitalize'
            }}>
              {planId}
            </span>
          </div>
          <div style={{ fontSize: 12, opacity: 0.6, marginTop: 4 }}>
            {data.language?.toUpperCase() || 'EN'}
            {data.parentEnvironment && data.parentEnvironment !== 'main' ? ` / Parent: ${data.parentEnvironment}` : ''}
            {plan.startDate ? ` / Since ${moment(plan.startDate).format('MMM D, YYYY')}` : ''}
            {data.subscriptionExpires ? ` / Expires ${moment(data.subscriptionExpires).format('MMM D, YYYY')}` : ''}
            {hasActiveTrial ? ` / Trial until ${moment(data.trialExpires).format('MMM D, YYYY')}` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {data.aiUsage && (
            <div style={{ textAlign: 'center', fontSize: 11, opacity: 0.7 }}>
              <div style={{ fontWeight: 700, fontSize: 16 }}>{data.aiUsage.callsThisWeek || 0}</div>
              AI calls/week
              {data.aiUsage.totalCallsAllTime > 0 && <div>{data.aiUsage.totalCallsAllTime} total</div>}
            </div>
          )}
        </div>
      </div>

      {/* Tab navigation */}
      <div className="tab-container">
        {tabs.map(tab => (
          <div
            key={tab.key}
            className={`tab-item${activeTab === tab.key ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            <i className={tab.icon} />
            {tab.label}
          </div>
        ))}
      </div>

      {/* ==================== OVERVIEW TAB ==================== */}
      {activeTab === 'overview' && (
        <>
          {/* Warnings */}
          {warnings.length > 0 && (
            <div className="content-bubble" style={{ marginTop: 0, padding: '16px 20px' }}>
              <SectionTitle icon="icon-alert" title="Alerts & Warnings" count={warnings.length} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {warnings.map((w, i) => <WarningBadge key={i} warning={w} />)}
              </div>
            </div>
          )}

          {/* KPI Row */}
          <div style={gridStyle()}>
            <div className="content-bubble kpi-bubble" style={{ ...bubbleInGrid, overflow: 'hidden' }}>
              <div style={{ display: 'flex', flexWrap: 'nowrap' }}>
                <KpiCard icon="icon-Risico" label="Risks" value={insights.totalRisks || 0} />
                <KpiCard icon="icon-Maatregelen" label="Measures" value={insights.totalMeasures || 0} subtitle={`${insights.measuresOverdue || 0} overdue`} />
                <KpiCard icon="icon-score" label="Avg Score" value={insights.avgRiskScore || 0} subtitle={`Max: ${insights.maxRiskScore || 0}`} />
                <KpiCard
                  icon="icon-arrow-down" label="Score Reduction"
                  value={`${insights.scoreReduction || 0}%`}
                  color={insights.scoreReduction > 0 ? 'var(--green-risk-score)' : insights.scoreReduction < 0 ? 'var(--red-risk-score)' : undefined}
                  subtitle="initial vs current"
                />
              </div>
            </div>
            <div className="content-bubble kpi-bubble" style={{ ...bubbleInGrid, overflow: 'hidden' }}>
              <div style={{ display: 'flex', flexWrap: 'nowrap' }}>
                <KpiCard icon="icon-Organisaties" label="Orgs" value={orgs.length} />
                <KpiCard icon="icon-Projecten" label="Domains" value={projects.length} />
                <KpiCard icon="icon-users" label="Users" value={users.length} subtitle={`${insights.usersInsecure || 0} no 2FA/SSO`} />
                <KpiCard icon="icon-SoA" label="SoA" value={insights.soaIncluded || 0} subtitle={`of ${insights.soaTotal || 0} controls`} />
              </div>
            </div>
          </div>

          {/* Main grid */}
          <div style={gridStyle()}>
            {/* Risk Severity Breakdown */}
            <div className="content-bubble" style={{ ...bubbleInGrid, padding: '20px 24px' }}>
              <SectionTitle icon="icon-pie" title="Risk Severity" />
              <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                <SeverityDot count={insights.riskSeverity?.high || 0} color="var(--red-risk-score)" label="High" />
                <SeverityDot count={insights.riskSeverity?.medium || 0} color="var(--yellow-risk-score)" label="Medium" />
                <SeverityDot count={insights.riskSeverity?.low || 0} color="var(--green-risk-score)" label="Low" />
              </div>
              {insights.totalRisks > 0 && (
                <div style={{ display: 'flex', height: 24, borderRadius: 6, overflow: 'hidden', marginBottom: 16 }}>
                  {insights.riskSeverity?.high > 0 && (
                    <div style={{
                      width: `${(insights.riskSeverity.high / insights.totalRisks) * 100}%`,
                      backgroundColor: 'var(--red-risk-score)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontSize: 11, fontWeight: 600
                    }}>
                      {Math.round((insights.riskSeverity.high / insights.totalRisks) * 100)}%
                    </div>
                  )}
                  {insights.riskSeverity?.medium > 0 && (
                    <div style={{
                      width: `${(insights.riskSeverity.medium / insights.totalRisks) * 100}%`,
                      backgroundColor: 'var(--yellow-risk-score)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontSize: 11, fontWeight: 600
                    }}>
                      {Math.round((insights.riskSeverity.medium / insights.totalRisks) * 100)}%
                    </div>
                  )}
                  {insights.riskSeverity?.low > 0 && (
                    <div style={{
                      width: `${(insights.riskSeverity.low / insights.totalRisks) * 100}%`,
                      backgroundColor: 'var(--green-risk-score)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontSize: 11, fontWeight: 600
                    }}>
                      {Math.round((insights.riskSeverity.low / insights.totalRisks) * 100)}%
                    </div>
                  )}
                </div>
              )}

              {/* CIA Triad */}
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.5 }}>CIA Impact Areas</div>
              <div style={{ display: 'flex', gap: 20 }}>
                {['confidentiality', 'integrity', 'availability'].map(key => (
                  <div key={key} style={{ textAlign: 'center' }}>
                    <div style={{
                      width: 48, height: 48, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      backgroundColor: 'var(--light-blue-bg-color)', fontWeight: 700, fontSize: 16, margin: '0 auto 4px'
                    }}>
                      {insights.ciaBreakdown?.[key] || 0}
                    </div>
                    <div style={{ fontSize: 11, opacity: 0.7, textTransform: 'capitalize' }}>{key.charAt(0).toUpperCase()}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Measures Status */}
            <div className="content-bubble" style={{ ...bubbleInGrid, padding: '20px 24px' }}>
              <SectionTitle icon="icon-Maatregelen" title="Measures Progress" count={insights.totalMeasures || 0} />
              {insights.totalMeasures > 0 ? (
                <>
                  <ProgressBar value={insights.measuresImplemented || 0} max={insights.totalMeasures} color="var(--green-risk-score)" label="Implemented" />
                  <ProgressBar value={insights.measuresInProgress || 0} max={insights.totalMeasures} color="var(--yellow-risk-score)" label="In Progress" />
                  <ProgressBar value={insights.measuresTodo || 0} max={insights.totalMeasures} color="#ccc" label="To Do" />
                  {insights.measuresOverdue > 0 && (
                    <div style={{
                      marginTop: 12, padding: '8px 12px', borderRadius: 6,
                      backgroundColor: '#fff0f0', border: '1px solid #ffcdd2',
                      fontSize: 12, color: 'var(--red-risk-score)', fontWeight: 500
                    }}>
                      <i className="icon-clock" style={{ marginRight: 6 }} />
                      {insights.measuresOverdue} measure{insights.measuresOverdue > 1 ? 's' : ''} past deadline
                    </div>
                  )}

                  {/* Treatment breakdown */}
                  <div style={{ marginTop: 20, fontSize: 12, fontWeight: 600, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Treatment Strategy</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {Object.entries(insights.risksByTreatment || {}).map(([key, count]) => (
                      <div key={key} style={{
                        padding: '8px 12px', borderRadius: 6, backgroundColor: '#f8f9fa',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                      }}>
                        <span style={{ fontSize: 12, textTransform: 'capitalize', fontWeight: 500 }}>{key}</span>
                        <span style={{ fontWeight: 700 }}>{count}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div style={{ fontSize: 13, opacity: 0.5, padding: '20px 0' }}>No measures registered</div>
              )}
            </div>

            {/* Top Risks */}
            <div className="content-bubble" style={{ ...bubbleInGrid, padding: '20px 24px' }}>
              <SectionTitle icon="icon-flame" title="Highest Scoring Risks" count={insights.topRisks?.length || 0} />
              {insights.topRisks && insights.topRisks.length > 0 ? (
                <table className="content-bubble-table" style={{ tableLayout: 'fixed' }}>
                  <thead>
                    <tr>
                      <td>Risk</td>
                      <td width="90">Owner</td>
                      <td width="60">Score</td>
                      <td width="70">Measures</td>
                    </tr>
                  </thead>
                  <tbody>
                    {insights.topRisks.slice(0, 7).map((risk, i) => (
                      <tr key={risk.id || i}>
                        <td className="align-left text-ellipsis" style={{ fontSize: 13 }}>
                          {i + 1}. {risk.description}
                        </td>
                        <td style={{ fontSize: 12 }}>{risk.owner || '-'}</td>
                        <td>
                          <div className={getScoreBorderClass(risk.score, rangeBounds)}>
                            {risk.score}
                          </div>
                        </td>
                        <td style={{ fontSize: 12 }}>{risk.measuresCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div style={{ fontSize: 13, opacity: 0.5, padding: '20px 0' }}>No risks registered</div>
              )}
            </div>

            {/* Owners */}
            <div className="content-bubble" style={{ ...bubbleInGrid, padding: '20px 24px' }}>
              <SectionTitle icon="icon-owner" title="Risk Owners" />
              {Object.keys(insights.risksByOwner || {}).length > 0 ? (
                <table className="content-bubble-table" style={{ tableLayout: 'fixed' }}>
                  <thead>
                    <tr>
                      <td>Owner</td>
                      <td width="50">Total</td>
                      <td width="50">High</td>
                      <td width="60">Medium</td>
                      <td width="50">Low</td>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(insights.risksByOwner)
                      .sort((a, b) => b[1].high - a[1].high || b[1].count - a[1].count)
                      .map(([owner, counts]) => (
                        <tr key={owner}>
                          <td className="align-left" style={{ fontSize: 13, fontWeight: 500 }}>{owner}</td>
                          <td style={{ fontWeight: 600 }}>{counts.count}</td>
                          <td style={{ color: counts.high > 0 ? 'var(--red-risk-score)' : undefined, fontWeight: counts.high > 0 ? 600 : 400 }}>{counts.high}</td>
                          <td style={{ color: counts.medium > 0 ? '#e65100' : undefined }}>{counts.medium}</td>
                          <td style={{ color: counts.low > 0 ? 'var(--green-risk-score)' : undefined }}>{counts.low}</td>
                        </tr>
                      ))}
                    {insights.risksWithoutOwner > 0 && (
                      <tr>
                        <td className="align-left" style={{ fontSize: 13, opacity: 0.6, fontStyle: 'italic' }}>Unassigned</td>
                        <td style={{ fontWeight: 600, opacity: 0.6 }}>{insights.risksWithoutOwner}</td>
                        <td colSpan={3} />
                      </tr>
                    )}
                  </tbody>
                </table>
              ) : (
                <div style={{ fontSize: 13, opacity: 0.5, padding: '20px 0' }}>No risk owners found</div>
              )}
            </div>
          </div>

          {/* Organizations */}
          <div className="content-bubble" style={{ padding: '20px 24px' }}>
            <SectionTitle icon="icon-Organisaties" title="Organizations" count={orgs.length} />
            {orgs.length > 0 ? (
              <table className="content-bubble-table">
                <thead>
                  <tr>
                    <td>Name</td>
                    <td width="120">Security Standard</td>
                    <td width="100">Risk Range</td>
                    <td width="80">SoA Controls</td>
                    <td width="80">Domains</td>
                  </tr>
                </thead>
                <tbody>
                  {orgs.map((org) => {
                    const domainCount = projects.filter(p => p.orgId === org.id).length;
                    return (
                      <tr key={org.id}>
                        <td className="align-left" style={{ fontWeight: 500 }}>{org.name}</td>
                        <td>{org.securityStandard || 'ISO27001'}</td>
                        <td>{org.rangeBounds ? `${org.rangeBounds[0]} - ${org.rangeBounds[1]}` : '-'}</td>
                        <td>{org.soa?.length || 0}</td>
                        <td style={{ fontWeight: 600 }}>{domainCount}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div style={{ fontSize: 13, opacity: 0.5 }}>No organizations</div>
            )}
          </div>
        </>
      )}

      {/* ==================== RISKS TAB ==================== */}
      {activeTab === 'risks' && (
        <>
          {/* Risk distribution matrix */}
          <div style={gridStyle()}>
            <div className="content-bubble" style={{ ...bubbleInGrid, padding: '20px 24px', marginTop: 0 }}>
              <SectionTitle icon="icon-matrix" title="Risk Distribution Matrix" />
              <RiskMatrix risks={risks} rangeBounds={rangeBounds} />
            </div>

            <div className="content-bubble" style={{ ...bubbleInGrid, padding: '20px 24px', marginTop: 0 }}>
              <SectionTitle icon="icon-transfer" title="Initial vs Current" />
              {insights.totalRisks > 0 ? (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-around', marginBottom: 20 }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 11, opacity: 0.6, fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Initial Total</div>
                      <div style={{ fontSize: 28, fontWeight: 700 }}>{insights.totalInitialScore}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <i className="icon-arrow-right" style={{ fontSize: 24, opacity: 0.3 }} />
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 11, opacity: 0.6, fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Current Total</div>
                      <div style={{ fontSize: 28, fontWeight: 700, color: insights.totalCurrentScore <= insights.totalInitialScore ? 'var(--green-risk-score)' : 'var(--red-risk-score)' }}>
                        {insights.totalCurrentScore}
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 11, opacity: 0.6, fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Reduction</div>
                      <div style={{
                        fontSize: 28, fontWeight: 700,
                        color: insights.scoreReduction > 0 ? 'var(--green-risk-score)' : insights.scoreReduction < 0 ? 'var(--red-risk-score)' : undefined
                      }}>
                        {insights.scoreReduction > 0 ? '-' : ''}{insights.scoreReduction}%
                      </div>
                    </div>
                  </div>

                  {/* Per-risk score comparison */}
                  <div style={{ fontSize: 12, fontWeight: 600, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Per-Risk Comparison (Top 8 by delta)</div>
                  <table className="content-bubble-table" style={{ tableLayout: 'fixed' }}>
                    <thead>
                      <tr>
                        <td>Risk</td>
                        <td width="60">Initial</td>
                        <td width="60">Current</td>
                        <td width="60">Delta</td>
                      </tr>
                    </thead>
                    <tbody>
                      {risks
                        .map(r => ({
                          description: r.description,
                          initial: parseInt(r['initial-chance'] || 1) * parseInt(r['initial-impact'] || 1),
                          current: parseInt(r['actual-chance'] || r['initial-chance'] || 1) * parseInt(r['actual-impact'] || r['initial-impact'] || 1)
                        }))
                        .map(r => ({ ...r, delta: r.current - r.initial }))
                        .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
                        .slice(0, 8)
                        .map((r, i) => (
                          <tr key={i}>
                            <td className="align-left text-ellipsis" style={{ fontSize: 12 }}>{r.description}</td>
                            <td style={{ fontSize: 12 }}>{r.initial}</td>
                            <td>
                              <div className={getScoreBorderClass(r.current, rangeBounds)} style={{ fontSize: 12 }}>
                                {r.current}
                              </div>
                            </td>
                            <td style={{
                              fontSize: 12, fontWeight: 600,
                              color: r.delta < 0 ? 'var(--green-risk-score)' : r.delta > 0 ? 'var(--red-risk-score)' : undefined
                            }}>
                              {r.delta > 0 ? '+' : ''}{r.delta}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ fontSize: 13, opacity: 0.5, padding: '20px 0' }}>No risks registered</div>
              )}
            </div>
          </div>

          {/* Full risks table */}
          <div className="content-bubble" style={{ padding: '20px 24px' }}>
            <SectionTitle icon="icon-list" title="All Risks" count={risks.length} />
            {risks.length > 0 ? (
              <table className="content-bubble-table" style={{ tableLayout: 'fixed' }}>
                <thead>
                  <tr>
                    <td>Description</td>
                    <td width="90">Owner</td>
                    <td width="55">Score</td>
                    <td width="70">Treatment</td>
                    <td width="65">Measures</td>
                    <td width="60">Approved</td>
                    <td width="40">C</td>
                    <td width="40">I</td>
                    <td width="40">A</td>
                  </tr>
                </thead>
                <tbody>
                  {risks
                    .map(r => ({
                      ...r,
                      _score: parseInt(r['actual-chance'] || r['initial-chance'] || 1) * parseInt(r['actual-impact'] || r['initial-impact'] || 1)
                    }))
                    .sort((a, b) => b._score - a._score)
                    .map((risk) => (
                      <tr key={risk.id}>
                        <td className="align-left text-ellipsis" style={{ fontSize: 12 }}>{risk.description}</td>
                        <td style={{ fontSize: 12 }}>{risk.owner || '-'}</td>
                        <td>
                          <div className={getScoreBorderClass(risk._score, rangeBounds)}>
                            {risk._score}
                          </div>
                        </td>
                        <td style={{ fontSize: 11, textTransform: 'capitalize' }}>{risk.treatment || 'reduce'}</td>
                        <td style={{ fontSize: 12 }}>{risk.measures?.length || 0}</td>
                        <td>
                          {risk.riskApproved
                            ? <i className="icon-check-circle" style={{ color: 'var(--green-risk-score)' }} />
                            : <i className="icon-cross-circle" style={{ color: '#ccc' }} />
                          }
                        </td>
                        <td>{risk.confidentiality ? <i className="icon-check" style={{ fontSize: 11, color: 'var(--text-color)' }} /> : <span style={{ opacity: 0.2 }}>-</span>}</td>
                        <td>{risk.integrity ? <i className="icon-check" style={{ fontSize: 11, color: 'var(--text-color)' }} /> : <span style={{ opacity: 0.2 }}>-</span>}</td>
                        <td>{risk.availability ? <i className="icon-check" style={{ fontSize: 11, color: 'var(--text-color)' }} /> : <span style={{ opacity: 0.2 }}>-</span>}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            ) : (
              <div style={{ fontSize: 13, opacity: 0.5 }}>No risks registered</div>
            )}
          </div>
        </>
      )}

      {/* ==================== DOMAINS TAB ==================== */}
      {activeTab === 'domains' && (
        <div className="content-bubble" style={{ padding: '20px 24px', marginTop: 0 }}>
          <SectionTitle icon="icon-Projecten" title="Domains (Projects)" count={projects.length} />
          {insights.projectsEnriched && insights.projectsEnriched.length > 0 ? (
            <table className="content-bubble-table">
              <thead>
                <tr>
                  <td>Domain</td>
                  <td width="130">Organization</td>
                  <td width="60">Risks</td>
                  <td width="50">High</td>
                  <td width="60">Medium</td>
                  <td width="50">Low</td>
                  <td width="70">Avg Score</td>
                  <td width="100">Created</td>
                </tr>
              </thead>
              <tbody>
                {insights.projectsEnriched
                  .sort((a, b) => b.highRisks - a.highRisks || b.risks - a.risks)
                  .map((proj) => (
                    <tr key={proj.id}>
                      <td className="align-left" style={{ fontWeight: 500 }}>{proj.name}</td>
                      <td style={{ fontSize: 12 }}>{proj.orgName}</td>
                      <td style={{ fontWeight: 600 }}>{proj.risks}</td>
                      <td style={{ color: proj.highRisks > 0 ? 'var(--red-risk-score)' : undefined, fontWeight: proj.highRisks > 0 ? 600 : 400 }}>{proj.highRisks}</td>
                      <td style={{ color: proj.mediumRisks > 0 ? '#e65100' : undefined }}>{proj.mediumRisks}</td>
                      <td style={{ color: proj.lowRisks > 0 ? 'var(--green-risk-score)' : undefined }}>{proj.lowRisks}</td>
                      <td>
                        {proj.risks > 0 && (
                          <div className={getScoreBorderClass(parseFloat(proj.avgScore), rangeBounds)} style={{ fontSize: 12 }}>
                            {proj.avgScore}
                          </div>
                        )}
                      </td>
                      <td style={{ fontSize: 12, opacity: 0.7 }}>{proj.createdAt ? moment(proj.createdAt).format('MMM D, YYYY') : '-'}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          ) : (
            <div style={{ fontSize: 13, opacity: 0.5 }}>No domains registered</div>
          )}
        </div>
      )}

      {/* ==================== USERS TAB ==================== */}
      {activeTab === 'users' && (
        <>
          {/* User role summary */}
          <div className="content-bubble kpi-bubble" style={{ overflow: 'hidden', marginTop: 0 }}>
            <div style={{ display: 'flex', flexWrap: 'nowrap' }}>
              {Object.entries(insights.userRoles || {}).map(([role, count]) => (
                <KpiCard key={role} icon="icon-user" label={role} value={count} />
              ))}
              <KpiCard
                icon="icon-shield"
                label="Secure Auth"
                value={`${users.length - (insights.usersInsecure || 0)}/${users.length}`}
                subtitle={insights.usersWithSSO > 0 ? `${insights.usersWithSSO} SSO` : undefined}
                color={(insights.usersInsecure || 0) > 0 ? 'var(--red-risk-score)' : 'var(--green-risk-score)'}
              />
            </div>
          </div>

          <div className="content-bubble" style={{ padding: '20px 24px' }}>
            <SectionTitle icon="icon-users" title="Users" count={users.length} />
            {users.length > 0 ? (
              <table className="content-bubble-table">
                <thead>
                  <tr>
                    <td>Name</td>
                    <td width="200">Email</td>
                    <td width="90">Role</td>
                    <td width="60">2FA</td>
                    <td width="60">SSO</td>
                    <td width="110">Organization</td>
                    <td width="110">Domain</td>
                    <td width="100">Created</td>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => {
                    const userOrg = orgs.find(o => o.id === user.orgId);
                    const userProject = projects.find(p => p.id === user.projectId);
                    const isSecure = user.twoFactorEnabled || user.ssoEnabled;
                    return (
                      <tr key={user.id}>
                        <td className="align-left" style={{ fontWeight: 500 }}>{user.name}</td>
                        <td style={{ fontSize: 12 }}>{user.email}</td>
                        <td>
                          <span style={{
                            display: 'inline-block', padding: '2px 8px', borderRadius: 10,
                            fontSize: 11, fontWeight: 600,
                            backgroundColor: user.role === 'admin' ? 'var(--light-blue-bg-color)' : '#f5f5f5',
                            color: user.role === 'admin' ? 'var(--text-color)' : 'var(--gray-text-color)'
                          }}>
                            {user.role}
                          </span>
                        </td>
                        <td>
                          {user.twoFactorEnabled
                            ? <i className="icon-check-circle" style={{ color: 'var(--green-risk-score)' }} />
                            : <i className="icon-cross-circle" style={{ color: isSecure ? '#ccc' : 'var(--red-risk-score)' }} />
                          }
                        </td>
                        <td>
                          {user.ssoEnabled
                            ? <i className="icon-check-circle" style={{ color: 'var(--green-risk-score)' }} />
                            : <i className="icon-cross-circle" style={{ color: isSecure ? '#ccc' : 'var(--red-risk-score)' }} />
                          }
                        </td>
                        <td style={{ fontSize: 12 }}>{userOrg?.name || '-'}</td>
                        <td style={{ fontSize: 12 }}>{userProject?.name || '-'}</td>
                        <td style={{ fontSize: 12, opacity: 0.7 }}>{user.createdAt ? moment(user.createdAt).format('MMM D, YYYY') : '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div style={{ fontSize: 13, opacity: 0.5 }}>No users</div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

/* 5x5 Risk Distribution Matrix */
const RiskMatrix = ({ risks, rangeBounds }) => {
  const matrix = Array.from({ length: 5 }, () => Array.from({ length: 5 }, () => 0));
  risks.forEach(risk => {
    const chance = parseInt(risk['actual-chance'] || risk['initial-chance'] || 0);
    const impact = parseInt(risk['actual-impact'] || risk['initial-impact'] || 0);
    if (chance >= 1 && chance <= 5 && impact >= 1 && impact <= 5) {
      matrix[chance - 1][impact - 1]++;
    }
  });

  const getCellColor = (score) => getRiskColorVar(score, rangeBounds);

  return (
    <div style={{ position: 'relative', paddingLeft: 24 }}>
      <div style={{ position: 'absolute', left: -2, top: '50%', transform: 'rotate(-90deg)', transformOrigin: '0 0', fontSize: 11, opacity: 0.7, whiteSpace: 'nowrap' }}>
        Impact
      </div>
      <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 3, tableLayout: 'fixed' }}>
        <thead>
          <tr>
            <th style={{ width: 30 }} />
            {[1, 2, 3, 4, 5].map(c => (
              <th key={c} style={{ fontSize: 11, textAlign: 'center', opacity: 0.7 }}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[1, 2, 3, 4, 5].map(impact => (
            <tr key={impact}>
              <td style={{ border: 0, fontSize: 11, textAlign: 'center', fontWeight: 600, opacity: 0.7, background: 'none' }}>{impact}</td>
              {[1, 2, 3, 4, 5].map(chance => {
                const score = chance * impact;
                const count = matrix[chance - 1][impact - 1];
                return (
                  <td key={chance} style={{
                    padding: 6, textAlign: 'center',
                    backgroundColor: getCellColor(score), color: '#fff',
                    fontWeight: 600, fontSize: 12, borderRadius: 3,
                    opacity: count > 0 ? 1 : 0.35
                  }}>
                    {count > 0
                      ? <div>{count} {count === 1 ? 'risk' : 'risks'}</div>
                      : <div style={{ opacity: 0.5 }}>{score}</div>
                    }
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ textAlign: 'center', marginTop: 4, fontSize: 11, opacity: 0.7 }}>Chance</div>
    </div>
  );
};

export default ViewEnvironmentPage;
